import React, { useMemo } from "react";
import type { Ref } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { Platform, View } from "react-native";
import type { AnimatedStyle } from "react-native-reanimated";
import Animated from "react-native-reanimated";

import Image from "$src/core/components/atoms/Image/Image";
import Text from "$src/core/components/atoms/Text/Text";
import useResponsiveImage from "$src/core/hooks/use-responsive-image";
import type { ImageDto } from "$src/core/models/dto";
import pickImageUrl from "$src/core/utils/image/pick-image-url";
import formatDuration from "$src/core/utils/time/format-duration";

import $ from "./Card.styles";

type Props = {
  type: "portrait" | "landscape";
  title?: string;
  subtitle?: string;
  image?: ImageDto;
  imageDimensions?: { width?: number; height?: number }[];
  duration?: number;
  progress?: number;
  cardStyle?: StyleProp<ViewStyle | AnimatedStyle<ViewStyle>>;
  ref?: Ref<View>;
};

const Card = React.memo(
  ({
    ref,
    type,
    title,
    subtitle,
    image,
    imageDimensions,
    duration,
    progress,
    cardStyle,
  }: Props) => {
    const cover = useMemo(() => {
      const fallbackType = type === "portrait" ? "landscape" : "portrait";
      return {
        uri: image ? pickImageUrl(image, [type, fallbackType]) : undefined,
        dimensions: imageDimensions,
      };
    }, [type, image, imageDimensions]);

    const responsiveCoverImage = useResponsiveImage(cover.uri, {
      dimensions: cover.dimensions,
    });

    const hasTitle = !!title;
    const hasSubtitle = !!subtitle;

    return (
      <View style={$.wrapper}>
        <Animated.View style={[$.card, cardStyle]} ref={ref}>
          <View style={$.imageContainer}>
            {/* This is a decorative image, so we don't need an accessibility label */}
            {/* eslint-disable-next-line triple-rn-a11y/accessibility-label */}
            <Image
              accessibilityRole="none"
              accessible={false}
              source={responsiveCoverImage}
              contentFit="cover"
              style={$.image}
            />
          </View>
          {duration && (
            <View style={$.duration}>
              <Text preset="tiny" accessibilityRole="text">
                {formatDuration(duration)}
              </Text>
            </View>
          )}
          {progress && duration && (
            <View style={$.progressBar}>
              <View style={[{ width: `${progress}%` }, $.progressAmount]} />
            </View>
          )}
        </Animated.View>

        {(hasTitle || hasSubtitle) && (
          <View style={$.metaContainer}>
            <View style={$.meta}>
              {title && (
                <Text
                  preset="heading5"
                  accessibilityRole="header"
                  numberOfLines={hasSubtitle ? 1 : 2}
                >
                  {title}
                </Text>
              )}
              {subtitle && (
                <Text
                  accessibilityRole={Platform.OS === "web" ? "text" : "summary"}
                  preset="tiny"
                  numberOfLines={1}
                  style={$.subtitle}
                >
                  {subtitle}
                </Text>
              )}
            </View>
          </View>
        )}
      </View>
    );
  },
);

export default Card;
